/**
 * Amplitude Configuration Utility
 * Provides environment detection and API key resolution for Amplitude services
 * This TypeScript version mirrors the logic in public/scripts/amplitudeConfig.js
 */

export enum Environment {
  Production = 'production',
  NonProduction = 'nonProduction',
}

export enum ServiceType {
  Analytics = 'analytics',
  VisualExperiments = 'visualExperiments',
}

/**
 * Detects the current environment based on hostname
 * @returns Environment name: 'production' if hostname contains mydatascope.com (excluding staging/testing), otherwise 'nonProduction'
 */
export function detectEnvironment(): Environment {
  const hostname = window.location.hostname;

  // Production: only when hostname contains mydatascope.com AND does NOT contain staging or testing
  if (
    hostname.includes('mydatascope.com') &&
    !hostname.includes('staging') &&
    !hostname.includes('testing') &&
    !hostname.includes('test')
  ) {
    return Environment.Production;
  }

  // Everything else is non-production (localhost, staging, testing, etc.)
  return Environment.NonProduction;
}

/**
 * Gets the appropriate Amplitude API key based on the current environment
 * All Amplitude services (analytics, visualExperiments, etc.) use the same API key
 * @param serviceType - Type of service (kept for backwards compatibility, but all use same key)
 * @returns API key for the current environment
 */
export function getAmplitudeApiKey(serviceType: ServiceType = ServiceType.Analytics): string {
  const environment = detectEnvironment();

  // API Keys configuration - same key for all Amplitude services
  const apiKeys: Record<Environment, string> = {
    [Environment.Production]: '6846867d9f6178955fb7929e84be476a',
    [Environment.NonProduction]: '422b8815b20a8bc3ae52923e0a58b020',
  };

  return apiKeys[environment] || apiKeys[Environment.NonProduction];
}

/**
 * Gets API key from window.AmplitudeConfig if available (from public script),
 * otherwise falls back to TypeScript implementation
 * @param serviceType - Type of service
 * @returns API key
 */
export function getAmplitudeApiKeyWithFallback(
  serviceType: ServiceType = ServiceType.Analytics,
): string {
  // Try to use the global config from public script first
  if (
    typeof window !== 'undefined' &&
    window.AmplitudeConfig &&
    typeof window.AmplitudeConfig.getApiKey === 'function'
  ) {
    return window.AmplitudeConfig.getApiKey(serviceType);
  }

  // Fallback to TypeScript implementation
  return getAmplitudeApiKey(serviceType);
}

